import { AttachmentMeta } from "@/indexedDB/types";
import { processFileAttachment } from "@/middleware/attachments";
import { useState, useEffect, useRef } from "react";

type UseFileAttachmentOptions = {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  attachment: AttachmentMeta;
  canExecute?: boolean;
};

const useFileAttachment = ({
  messageId,
  messageTimestamp,
  chatId,
  attachment,
  canExecute = true,
}: UseFileAttachmentOptions) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [attachmentBlob, setAttachmentBlob] = useState<Blob | null>(null);

  // Churning per-message values via a ref so a live-query re-emit (new object,
  // same media) doesn't re-run the fetch; the effect keys on the STABLE
  // attachment identity instead. Same pattern as useMediaAttachment.
  const latest = useRef({ chatId, messageId, messageTimestamp, attachment });
  latest.current = { chatId, messageId, messageTimestamp, attachment };
  const attachmentId = attachment.attachmentId;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    // Guards against a superseded/unmounted effect publishing a stale blob.
    let active = true;

    // Reset on a real item change so a new item never briefly shows the
    // previous blob (e.g. PDF A still visible while PDF B loads).
    setAttachmentBlob(null);
    if (!canExecute) {
      // Nothing to fetch — settle to idle rather than sitting on the initial
      // `loading = true` (or a previous item's blob) forever.
      setLoading(false);
      return () => {
        active = false;
        controller.abort();
      };
    }
    setLoading(true);

    const attachmentInit = async () => {
      const {
        chatId: cId,
        messageId: mId,
        messageTimestamp: ts,
        attachment: meta,
      } = latest.current;
      // processFileAttachment resolves null (never throws) on abort/failure.
      const _attachmentBlob = await processFileAttachment({
        chatId: cId,
        messageId: mId,
        timestamp: ts,
        attachmentMeta: meta,
        signal,
      });
      if (!active) {
        return;
      }
      setAttachmentBlob(_attachmentBlob);
      setLoading(false);
    };
    attachmentInit();

    return () => {
      active = false;
      // Cancel the request when the component unmounts / the identity changes.
      controller.abort();
    };
  }, [attachmentId, canExecute]);

  return { loading, attachmentBlob };
};

export default useFileAttachment;
